import type { ReactNode } from "react";
import { MAIN_CONTENT_ID, SkipLink } from "~/components/a11y/skip-link";
import { BrazilAtlasBand, BrazilAuthMap } from "~/components/auth/brazil-auth-map";
import { Icon, type IconName } from "~/components/ui/icon";
import { LanguagePicker } from "~/components/ui/language-picker";
import { type MessageKey, useI18n } from "~/i18n";
import { BRAZIL_MAP_ATTRIBUTION_URL } from "~/lib/domain/brazil-geometry";
import { WATCHED_SEFAZ_COUNT } from "~/lib/domain/watched-sefaz";

const SUPPORT_EMAIL = "tax-platform@backchannel.com.br";

/** Id of the shell's persistent note, so sign-in controls can reference it. */
export const AUTH_NOTE_ID = "tax-auth-note";

/** What the product does, next to the access: three short, factual points. */
const AUTH_VALUE_POINTS: ReadonlyArray<{ icon: IconName; key: MessageKey }> = [
  { icon: "calculator", key: "auth.value.calculation" },
  { icon: "evidence", key: "auth.value.evidence" },
  { icon: "agents", key: "auth.value.research" },
];

/**
 * Pre-session frame for Tax Operations.
 *
 * One composition centred as a unit: the sign-in card (which carries the
 * TaxChannel mark and the product name) and, from 1024px, a panel of equal
 * height holding the green Brazil map, the São Paulo office mark and three
 * product value points. Below 1024px a compact green band (small silhouette,
 * context line, the same points) follows the card, so the territorial
 * identity never disappears. Header and footer align to the same measure.
 * Password, passkey and Google all go to the Tax Admin API.
 */
export function TaxAuthShell({
  title,
  description,
  note,
  children,
  variant = "default",
}: {
  title: string;
  description?: string;
  /** Persistent 13px help under the description (the login's domain rule). */
  note?: string;
  children: ReactNode;
  variant?: "default" | "loading" | "recovery";
}) {
  const { t } = useI18n();
  const context = t("auth.atlas.context", { count: WATCHED_SEFAZ_COUNT });

  return (
    <>
      <SkipLink />
      <main
        id={MAIN_CONTENT_ID}
        className={`tax-auth-shell tax-auth-shell--${variant}`}
        tabIndex={-1}
        aria-labelledby="tax-auth-title"
      >
        <div className="tax-auth-shell__frame">
          <header className="tax-auth-shell__topbar">
            <LanguagePicker compact />
          </header>

          <div className="tax-auth-shell__stage">
            <div className="tax-auth-shell__set">
              <section className="tax-auth-shell__content">
                <div className="tax-auth-shell__content-heading">
                  <p className="tax-auth-shell__lockup">
                    <span className="tax-auth-shell__lockup-tile" aria-hidden="true">
                      <img src="/taxchannel-mark.svg" alt="" width="32" height="32" />
                    </span>
                    <span className="tax-auth-shell__lockup-text">
                      <span className="tax-auth-shell__lockup-product">{t("app.brand")}</span>
                      <span className="tax-auth-shell__lockup-org">{t("app.name")}</span>
                    </span>
                  </p>
                  <h1 id="tax-auth-title">{title}</h1>
                  {description && <p className="tax-auth-shell__description">{description}</p>}
                  {note && (
                    <p id={AUTH_NOTE_ID} className="tax-auth-shell__note">
                      {note}
                    </p>
                  )}
                </div>
                {children}
              </section>

              <aside className="tax-auth-shell__figure" aria-label={t("auth.map.title")}>
                <p className="tax-auth-shell__figure-title">{t("auth.atlas.title")}</p>
                <BrazilAuthMap />
                <AuthValuePoints variant="figure" />
                <div className="tax-auth-shell__figure-foot">
                  <p className="tax-auth-shell__figure-context">{context}</p>
                  <a
                    className="tax-auth-shell__attribution"
                    href={BRAZIL_MAP_ATTRIBUTION_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("overview.coverage.attribution")}
                  </a>
                </div>
              </aside>

              <BrazilAtlasBand context={context}>
                <AuthValuePoints variant="band" />
              </BrazilAtlasBand>
            </div>
          </div>

          <footer className="tax-auth-shell__footer">
            <span className="tax-auth-shell__secure">
              <ShieldIcon />
              {t("auth.brand.secure")}
            </span>
            <a className="tax-auth-shell__help" href="/landing/">
              {t("auth.brand.about")}
            </a>
            <span className="tax-auth-shell__help">
              {t("auth.brand.help")} <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </span>
          </footer>
        </div>
      </main>
    </>
  );
}

function AuthValuePoints({ variant }: { variant: "figure" | "band" }) {
  const { t } = useI18n();
  return (
    <ul
      className={`tax-auth-values tax-auth-values--${variant}`}
      aria-label={t("auth.value.label")}
    >
      {AUTH_VALUE_POINTS.map(({ icon, key }) => (
        <li key={key}>
          <span className="tax-auth-values__icon" aria-hidden="true">
            <Icon name={icon} size={16} />
          </span>
          <span>{t(key)}</span>
        </li>
      ))}
    </ul>
  );
}

function ShieldIcon() {
  return (
    <svg className="tax-auth-shell__secure-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3 5 6v5.5c0 4.3 2.9 8.1 7 9.5 4.1-1.4 7-5.2 7-9.5V6l-7-3Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}
